import React, { useState, useEffect } from 'react'
import Modal from '../modal/modal'
import CloseSVG from '../../assets/svg/close.svg'
import { useAddEmployeeAdmin } from '../../redux/employees/hook'
import FormSwitch from '../form-switch'
import Button from '../button'

const PERMISSIONS = [
  {
    label: 'Employee',
    readKey: 'employee_read',
    writeKey: 'employee_write',
    defaultReadKey: 'permission_employee_read',
    defaultWriteKey: 'permission_employee_write',
  },
  {
    label: 'Salary',
    readKey: 'salary_read',
    writeKey: 'salary_write',
    defaultReadKey: 'permission_salary_read',
    defaultWriteKey: 'permission_salary_write',
  },
]

const AddJuniorAdmin = ({ isOpen, closeModal, employee }) => {
  const { mutateAsync: addAdmin, isLoading } = useAddEmployeeAdmin()

  const isUpdate =
    !!employee?.permission_employee_read ||
    !!employee?.permission_employee_write ||
    !!employee?.permission_salary_read ||
    !!employee?.permission_salary_write

  const [permissions, setPermissions] = useState({
    employee_read: 0,
    employee_write: 0,
    salary_read: 0,
    salary_write: 0,
  })

  // Populate from employee prop on open
  useEffect(() => {
    if (employee) {
      setPermissions({
        employee_read: employee.permission_employee_read ?? 0,
        employee_write: employee.permission_employee_write ?? 0,
        salary_read: employee.permission_salary_read ?? 0,
        salary_write: employee.permission_salary_write ?? 0,
      })
    }
  }, [employee, isOpen])

  const toggle = (key) => {
    setPermissions((prev) => ({ ...prev, [key]: prev[key] === 1 ? 0 : 1 }))
  }

  const handleSubmit = async () => {
    await addAdmin({
      employee_id: employee.id,
      ...permissions,
    })
    closeModal()
  }

  const employeeName = employee
    ? `${employee.first_name} ${employee.last_name}`
    : 'this employee'

  return (
    <Modal
      scrollable={true}
      className='!max-w-[600px] !rounded-none'
      isOpen={isOpen}
      centered={true}
    >
      <div className='flex flex-col overflow-auto'>
        {/* Header */}
        <div className='pl-8 pr-8 pt-4'>
          <div className='flex flex-row justify-between items-center'>
            <span className='header-4'>
              {isUpdate ? 'Update Junior Admin' : 'Add Junior Admin'}
            </span>
            <div className='cursor-pointer' onClick={closeModal}>
              <img src={CloseSVG} alt='close' />
            </div>
          </div>

          {/* Contextual description */}
          <p className='text-sm text-gray-500 mt-1'>
            {isUpdate
              ? `You are about to update ${employeeName}'s junior admin permissions. Adjust the toggles below and save your changes.`
              : `You are about to grant ${employeeName} junior admin access. Select the permissions you'd like to assign.`}
          </p>
        </div>

        {/* Permission Toggles */}
        <div className='flex flex-col gap-4 px-8 py-6'>
          {PERMISSIONS.map(({ label, readKey, writeKey }) => (
            <div key={label} className='flex flex-col gap-2'>
              <span className='font-semibold text-gray-700'>{label}</span>
              <div className='flex gap-8'>
                <FormSwitch
                  label='Read'
                  name={readKey}
                  checked={permissions[readKey] === 1}
                  onClick={() => toggle(readKey)}
                  classNames='flex gap-3 p5-medium !pb-0'
                />
                <FormSwitch
                  label='Write'
                  name={writeKey}
                  checked={permissions[writeKey] === 1}
                  onClick={() => toggle(writeKey)}
                  classNames='flex gap-3 p5-medium !pb-0'
                />
              </div>
            </div>
          ))}
        </div>

        {/* Submit */}
        <div className='px-8 pb-6'>
          <Button
            text={isUpdate ? 'Update Junior Admin' : 'Add as Junior Admin'}
            type='button'
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  )
}

export default AddJuniorAdmin
